import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  MagnifyingGlassIcon,
  EyeIcon,
  UserIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { messageService, Conversation } from '../services/messageService';
import { userService, User } from '../services/userService';

const AdminMessages: React.FC = () => {
  const { currentUser, userProfile } = useAuthContext();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('lastMessage.timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  usePageTitle('Admin Messages | BizBuyMax.com');

  useEffect(() => {
    const loadConversations = async () => {
      try {
        setLoading(true);
        // For admin, we need to get all conversations
        // This might need to be updated in the messageService
        const allConversations = await messageService.getAllConversations();
        const allUsers = await userService.getAllUsers();
        setConversations(allConversations);
        setUsers(allUsers);
      } catch (error) {
        console.error('Error loading conversations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, []);

  // Check if user is admin
  const isAdmin = currentUser && userProfile?.profile.userType === 'admin';

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">⚠️ Access Denied</div>
          <p className="text-gray-600 mb-4">You don't have permission to access the admin area.</p>
          <Link to="/" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  // Filter and sort conversations
  const filteredConversations = conversations
    .filter(conversation => {
      const matchesSearch = 
        (conversation.businessTitle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        conversation.participants.some(p => p.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'active' && conversation.status === 'active') ||
        (filterStatus === 'archived' && conversation.status === 'archived');
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'businessTitle':
          aValue = a.businessTitle || '';
          bValue = b.businessTitle || '';
          break;
        case 'lastMessage.timestamp':
          aValue = a.lastMessage?.timestamp;
          bValue = b.lastMessage?.timestamp;
          break;
        case 'participants':
          aValue = a.participants.length;
          bValue = b.participants.length;
          break;
        default:
          aValue = a.lastMessage?.timestamp;
          bValue = b.lastMessage?.timestamp;
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getMessagePreview = (content: string) => {
    return content.length > 50 ? content.substring(0, 50) + '...' : content;
  };

  const getTimeAgo = (timestamp: any) => {
    if (!timestamp) return 'No messages';
    
    const now = new Date();
    const messageTime = timestamp.toDate();
    const diffInHours = Math.floor((now.getTime() - messageTime.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return `${Math.floor(diffInHours / 168)}w ago`;
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      return `${user.profile.firstName} ${user.profile.lastName}`;
    }
    return userId; // Fallback to ID if user not found
  };

  return (
    <>
      <Helmet>
        <title>Admin Messages | BizBuyMax.com</title>
        <meta name="description" content="Manage messages and conversations in the admin dashboard" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Manage Messages</h1>
                <p className="text-gray-600 mt-1">View and manage all conversations</p>
              </div>
              <div className="flex items-center space-x-4">
                <Link
                  to="/admin/dashboard"
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Back to Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Conversations</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div className="text-sm text-gray-600">
                {filteredConversations.length} of {conversations.length} conversations
              </div>
            </div>
          </div>

          {/* Conversations Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading conversations...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('businessTitle')}>
                        <div className="flex items-center space-x-1">
                          <span>Business</span>
                          {sortField === 'businessTitle' && (
                            sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Participants</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Message</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('lastMessage.timestamp')}>
                        <div className="flex items-center space-x-1">
                          <span>Last Activity</span>
                          {sortField === 'lastMessage.timestamp' && (
                            sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredConversations.map((conversation) => (
                      <tr key={conversation.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-primary-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {conversation.businessTitle}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {conversation.businessId}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            {conversation.participants.map((participant, index) => (
                              <div key={index} className="flex items-center text-sm">
                                <UserIcon className="h-4 w-4 mr-1 text-gray-400" />
                                <span className="text-gray-900">{getUserName(participant)}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {conversation.lastMessage ? (
                              <>
                                <div className="font-medium">{getUserName(conversation.lastMessage.senderId)}</div>
                                <div className="text-gray-500">
                                  {getMessagePreview(conversation.lastMessage.content)}
                                </div>
                              </>
                            ) : (
                              <span className="text-gray-400">No messages yet</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {conversation.lastMessage ? 
                              getTimeAgo(conversation.lastMessage.timestamp) : 
                              'No activity'
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/admin/conversations/${conversation.id}`}
                              className="text-primary-600 hover:text-primary-900 p-1"
                              title="View Conversation"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminMessages; 